import { useCallback } from 'react'

import { colors } from '../../../theme/colors'
import { withUnicodeRouteArrow } from '../../../utils/routeLabelDisplay'
import { FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX } from '../chartTooltip/ChartTooltipPointerContext'

/**
 * Custom Recharts axis tick: truncates long labels and shows the full label in a
 * cursor-anchored tooltip styled like floating chart tooltips.
 *
 * Recharts passes ticks as SVG (`<g>` / `<text>`), not React DOM under Chakra.
 * Hosting the same portal tooltip as cartesian charts would require a bridge out
 * of SVG on every tick; a single shared `document.body` div is the usual pattern.
 */
export type TruncatedAxisTickProps = {
  x?: number
  y?: number
  payload?: { value: string }
  maxLength?: number
  angle?: number
  textColor?: string
  fontSize?: number
  fontWeight?: number
  fontFamily?: string
  fill?: string
  dy?: number
  dx?: number
  /** Passed to SVG `<text>` (default `end`). */
  textAnchor?: 'start' | 'middle' | 'end' | 'inherit'
}

const TOOLTIP_ELEMENT_ID = 'chart-axis-full-label-tooltip'
const AXIS_TOOLTIP_TEST_ID = 'floating-chart-axis-tooltip'

const floatingAxisTooltipStyles: Record<string, string> = {
  position: 'fixed',
  color: colors.presets.neutral[0],
  padding: '8px 12px',
  fontSize: '12px',
  fontWeight: '500',
  pointerEvents: 'none',
  zIndex: '9999',
  whiteSpace: 'nowrap',
  boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
  transition: 'opacity 0.15s',
  background: colors.presets.overlay.turquoise100_95,
  borderRadius: '0',
}

const clampAxisTooltip = (left: number, top: number, el: HTMLElement) => {
  const pad = 8
  const rect = el.getBoundingClientRect()
  const w = rect.width
  const h = rect.height
  const maxL = Math.max(pad, window.innerWidth - w - pad)
  const maxT = Math.max(pad, window.innerHeight - h - pad)
  return {
    left: Math.min(Math.max(pad, left), maxL),
    top: Math.min(Math.max(pad, top), maxT),
  }
}

const positionAxisTooltip = (
  el: HTMLElement,
  clientX: number,
  clientY: number,
) => {
  const left = clientX + FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX
  const top = clientY + FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX
  el.style.left = `${left}px`
  el.style.top = `${top}px`
  const { left: cl, top: ct } = clampAxisTooltip(left, top, el)
  el.style.left = `${cl}px`
  el.style.top = `${ct}px`
}

const getOrCreateAxisTooltipElement = (): HTMLDivElement => {
  const existing = document.getElementById(TOOLTIP_ELEMENT_ID)
  if (existing instanceof HTMLDivElement) return existing

  const el = document.createElement('div')
  el.id = TOOLTIP_ELEMENT_ID
  el.setAttribute('data-testid', AXIS_TOOLTIP_TEST_ID)
  Object.assign(el.style, floatingAxisTooltipStyles)
  document.body.appendChild(el)
  return el
}

const showAxisFullLabelTooltip = (
  text: string,
  clientX: number,
  clientY: number,
) => {
  const el = getOrCreateAxisTooltipElement()
  el.textContent = text
  positionAxisTooltip(el, clientX, clientY)
  el.style.display = 'block'
  el.style.opacity = '1'
}

const hideAxisFullLabelTooltip = () => {
  const el = document.getElementById(TOOLTIP_ELEMENT_ID)
  if (el) {
    el.style.opacity = '0'
    el.style.display = 'none'
  }
}

export const TruncatedAxisTick = ({
  x = 0,
  y = 0,
  payload,
  maxLength = 16,
  angle = -45,
  textColor = 'white',
  fontSize = 10,
  fontWeight = 600,
  fontFamily,
  dx = 0,
  dy = 6,
  textAnchor = 'end',
}: TruncatedAxisTickProps) => {
  const raw = withUnicodeRouteArrow(String(payload?.value ?? ''))
  const truncated =
    raw.length > maxLength ? raw.slice(0, maxLength) + '...' : raw
  const onEnter = useCallback(
    (e: React.MouseEvent) => {
      if (raw) showAxisFullLabelTooltip(raw, e.clientX, e.clientY)
    },
    [raw],
  )

  const onMove = useCallback((e: React.MouseEvent) => {
    const el = document.getElementById(TOOLTIP_ELEMENT_ID)
    if (!el) return
    positionAxisTooltip(el, e.clientX, e.clientY)
  }, [])

  return (
    <g
      transform={`translate(${x + dx},${y})`}
      onMouseEnter={onEnter}
      onMouseMove={onMove}
      onMouseLeave={hideAxisFullLabelTooltip}
      style={{ cursor: 'pointer' }}
    >
      <text
        dy={dy}
        textAnchor={textAnchor}
        fill={textColor}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        transform={`rotate(${angle})`}
      >
        {truncated}
      </text>
    </g>
  )
}
